import ScheduleSendIcon from '@material-symbols/svg-300/outlined/schedule_send.svg'
import VolunteerActivismIcon from '@material-symbols/svg-300/outlined/volunteer_activism.svg'
import classnames from 'classnames'
import { isBefore } from 'date-fns'
import { FormikProvider, useFormik } from 'formik'
import React, { useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { AnonymousLearningItem } from '../../../models/AnonymousLearningItem'
import { ScheduledCourseEventStatus } from '../../../models/BaseModels'
import { userCanAssignLearningItems } from '../../../state/calculations/permissions/userCan'
import { useGroup, useLearningItems } from '../../../state/entities'
import useAddGroupsToLearningItems from '../../../state/mutations/useAddGroupsToLearningItems'
import { useSession } from '../../../state/session'
import useFocusInputWithError from '../../../utilities/useFocusInputWithError'
import { CheckboxFormik } from '../../basics/Checkbox/Checkbox'
import FieldError from '../../basics/FieldError/FieldError'
import FormButtons from '../../basics/FormButtons/FormButtons'
import { TextInputFormik } from '../../basics/TextInput/TextInput'
import { SelectGroupsFormik } from '../../formElements/SelectGroups/SelectGroups'
import {
  SelectLearningItemFormik,
  SelectLearningItemPathFormik
} from '../../formElements/SelectLearningItem/SelectLearningItem'

import styles from './CreateGroupLearningAssignmentsForm.module.scss'

interface CreateGroupLearningAssignmentsFormProps {
  groupUuids?: string[]
  learningItemUuid?: string
  onClose: () => void
  pathsOrCourses?: boolean
}

export default React.memo(function CreateUserLearningAssignmentsForm({
  groupUuids,
  learningItemUuid,
  onClose,
  pathsOrCourses,
}: CreateGroupLearningAssignmentsFormProps) {
  const { t } = useTranslation()
  const { user: currentUser } = useSession()
  const { group } = useGroup(groupUuids?.[0])

  // Form
  const addGroupsToLearningItems = useAddGroupsToLearningItems()
  const formik = useFormik({
    initialValues: {
      learningItemUuid: learningItemUuid || '',
      groupUuids: groupUuids || [],
      action: 'suggest',
      dueDate: null as Date | null,
    },
    enableReinitialize: true,
    onSubmit: async (values, { setStatus }) => {
      setStatus()
      try {
        await addGroupsToLearningItems.mutateAsync({
          groupUuids: values.groupUuids,
          learningItemsAssignments: [
            {
              uuid: values.learningItemUuid,
              dueDate: values.action === 'assign' ? values.dueDate || undefined : undefined,
            },
          ],
        })

        onClose()
      } catch (err) {
        setStatus(String(err))
      }
    },
  })

  useFocusInputWithError(formik)

  const {
    learningItems: [learningItem],
  } = useLearningItems(formik.values.learningItemUuid ? [formik.values.learningItemUuid] : [])

  const now = useMemo(() => new Date(), [])

  const mapLearningItemSuggestions = useCallback(
    (item: AnonymousLearningItem) => {
      if (
        item.status === 'published' &&
        (userCanAssignLearningItems(currentUser) ||
          // On the backend, the group is filled with the permissions
          // that the current user has for the group
          group.permissions.assign_learning_item)
      ) {
        return { disabled: false }
      }
      return {
        disabled: true,
        info:
          item.status !== 'published'
            ? t(`learning item.status.${item.status}`)
            : t(`manage learning.learning item not allowed`),
      }
    },
    [currentUser]
  )

  return (
    <FormikProvider value={formik}>
      <form noValidate onSubmit={formik.handleSubmit} className={classnames(styles.CreateGroupLearningAssignmentsForm)}>
        <header>
          <hgroup>
            <h1>{t('manage learning.assign learning items')}</h1>
          </hgroup>
        </header>

        <div>
          <h2>
            {t(
              pathsOrCourses ? 'manage learning.step select learning path' : 'manage learning.step select learning item'
            )}
          </h2>

          {!pathsOrCourses && (
            <SelectLearningItemFormik name="learningItemUuid" required mapSuggestion={mapLearningItemSuggestions} />
          )}
          {pathsOrCourses && (
            <SelectLearningItemPathFormik name="learningItemUuid" required mapSuggestion={mapLearningItemSuggestions} />
          )}
        </div>

        <div>
          <h2>{t('manage learning.step select groups')}</h2>

          <SelectGroupsFormik name="groupUuids" required />
        </div>

        <div>
          <h2>{t('manage learning.step select action')}</h2>

          <div className={styles.selectAction}>
            <p>{t('manage learning.description select action')}</p>
            <div className={styles.options}>
              <div>
                <CheckboxFormik
                  id="action-suggest"
                  plain
                  type="radio"
                  required
                  name="action"
                  value="suggest"
                  label={t('manage learning.action suggest')}
                />
                <label htmlFor="action-suggest">
                  <VolunteerActivismIcon />
                  {t('manage learning.action suggest')}
                </label>
              </div>
              <div>
                <CheckboxFormik
                  id="action-assign"
                  plain
                  type="radio"
                  required
                  name="action"
                  value="assign"
                  label={t('manage learning.action assign')}
                  omitErrorDisplay
                />
                <label htmlFor="action-assign">
                  <ScheduleSendIcon />
                  {t('manage learning.action assign')}
                </label>
              </div>
            </div>
            {formik.values.action === 'suggest' && (
              <p>{t('manage learning.description suggest learning item for user')}</p>
            )}
            {formik.values.action === 'assign' && (
              <p>{t('manage learning.description assign learning item to user')}</p>
            )}
            <FieldError error={formik.errors.action} touched={formik.touched.action} />
          </div>
        </div>

        {formik.values.action === 'assign' && (
          <div className={styles.dueDate}>
            <h2>{t('manage learning.step set due date')}</h2>
            <TextInputFormik name="dueDate" label={t('learning item.due date')} type="date" required />
          </div>
        )}

        {learningItem?.kind === 'scheduledCourse' && (
          <div className={styles.availableEvents}>
            <h2>{t('manage learning.available dates')}</h2>
            <div className={styles.eventGrid}>
              {learningItem.scheduledCourse.events.map((event) => (
                <div
                  key={event.uuid}
                  className={classnames({
                    [styles.unavailable]:
                      isBefore(event.startDate, now) || event.status !== ScheduledCourseEventStatus.Bookable,
                  })}
                >
                  <div>
                    <span>{t('format.DATERANGE', { range: { start: event.startDate, end: event.endDate } })}</span>
                    <span>
                      {t('learning item.{count} scheduled course event seats available', {
                        count: event.seatsAvailable,
                      })}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        <FormButtons onClose={onClose} {...formik} isValid={true} />
      </form>
    </FormikProvider>
  )
})
